/**
 * Side panel pour gérer les patterns de filtrage email
 * Design cohérent avec AgentsPage (statsDrawerOpen)
 */
import { useState, useEffect } from 'react'
import { X, Plus, Trash2, Filter, Loader2, Edit2 } from 'lucide-react'
import { useEmailPatterns, useCreateEmailPattern, useUpdateEmailPattern, useDeleteEmailPattern } from '../../hooks/useEmailConnections'
import type { EmailConnection, EmailPattern, PatternFieldType, PatternAction } from '../../types/email'

interface Props {
  connection: EmailConnection
  onClose: () => void
}

const fieldTypes: { value: PatternFieldType; label: string }[] = [
  { value: 'subject', label: 'Sujet' },
  { value: 'from', label: 'Expéditeur' },
  { value: 'body', label: 'Corps' },
  { value: 'to', label: 'Destinataire' },
]

const actions: { value: PatternAction; label: string; color: string }[] = [
  { value: 'auto_reply', label: 'Réponse auto', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' },
  { value: 'require_confirmation', label: 'Confirmation', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300' },
  { value: 'ignore', label: 'Ignorer', color: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400' },
  { value: 'forward', label: 'Transférer', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' },
]

export function EmailPatternsModal({ connection, onClose }: Props) {
  const { data: patterns, isLoading } = useEmailPatterns(connection.id)
  const createPattern = useCreateEmailPattern()
  const updatePattern = useUpdateEmailPattern()
  const deletePattern = useDeleteEmailPattern()

  const [showForm, setShowForm] = useState(false)
  const [editingPattern, setEditingPattern] = useState<EmailPattern | null>(null)
  const [formData, setFormData] = useState({
    field_type: 'subject' as PatternFieldType,
    pattern: '',
    is_regex: false,
    action: 'auto_reply' as PatternAction,
    priority: 0,
    description: '',
  })

  // ESC to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const resetForm = () => {
    setFormData({
      field_type: 'subject',
      pattern: '',
      is_regex: false,
      action: 'auto_reply',
      priority: 0,
      description: '',
    })
    setEditingPattern(null)
    setShowForm(false)
  }

  const handleEdit = (pattern: EmailPattern) => {
    setFormData({
      field_type: pattern.field_type,
      pattern: pattern.pattern,
      is_regex: pattern.is_regex,
      action: pattern.action,
      priority: pattern.priority,
      description: pattern.description || '',
    })
    setEditingPattern(pattern)
    setShowForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (editingPattern) {
      await updatePattern.mutateAsync({
        patternId: editingPattern.id,
        connectionId: connection.id,
        ...formData,
      })
    } else {
      await createPattern.mutateAsync({
        connectionId: connection.id,
        ...formData,
      })
    }

    resetForm()
  }

  const handleDelete = async (pattern: EmailPattern) => {
    if (confirm('Supprimer ce pattern ?')) {
      await deletePattern.mutateAsync({
        patternId: pattern.id,
        connectionId: connection.id,
      })
    }
  }

  const handleToggleActive = async (pattern: EmailPattern) => {
    await updatePattern.mutateAsync({
      patternId: pattern.id,
      connectionId: connection.id,
      is_active: !pattern.is_active,
    })
  }

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm" onClick={onClose} aria-hidden />
      
      {/* Side Panel */}
      <aside className="fixed inset-y-0 right-0 z-[80] w-full max-w-xl border-l border-emerald-100/80 bg-white/95 shadow-2xl transition-transform dark:border-emerald-900/40 dark:bg-zinc-900/95">
        <div className="flex h-full flex-col">
          {/* Header */}
          <header className="flex items-start justify-between gap-4 border-b border-emerald-100/80 px-6 py-4 dark:border-emerald-900/40">
            <div className="space-y-1">
              <h2 className="text-xl font-semibold text-zinc-900 dark:text-white flex items-center gap-2">
                <Filter className="h-5 w-5 text-emerald-500" />
                Patterns de filtrage
              </h2>
              <p className="text-sm text-zinc-600 dark:text-zinc-300">{connection.email_address}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-2 text-zinc-500 transition hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
            >
              <X className="h-5 w-5" />
            </button>
          </header>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {/* Add Button */}
            {!showForm && (
              <button
                onClick={() => setShowForm(true)}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-emerald-200/70 text-emerald-600 hover:border-emerald-400 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-400 dark:hover:border-emerald-600 dark:hover:bg-emerald-900/20 transition-colors"
              >
                <Plus className="h-5 w-5" />
                Ajouter un pattern
              </button>
            )}

            {/* Form */}
            {showForm && (
              <form onSubmit={handleSubmit} className="p-4 rounded-2xl bg-emerald-50/50 border border-emerald-100/70 dark:bg-emerald-900/10 dark:border-emerald-900/40 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                      Champ
                    </label>
                    <select
                      value={formData.field_type}
                      onChange={(e) => setFormData({ ...formData, field_type: e.target.value as PatternFieldType })}
                      className="w-full px-3 py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    >
                      {fieldTypes.map((f) => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                      Action
                    </label>
                    <select
                      value={formData.action}
                      onChange={(e) => setFormData({ ...formData, action: e.target.value as PatternAction })}
                      className="w-full px-3 py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    >
                      {actions.map((a) => (
                        <option key={a.value} value={a.value}>{a.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                    Pattern {formData.is_regex && '(regex)'}
                  </label>
                  <input
                    type="text"
                    value={formData.pattern}
                    onChange={(e) => setFormData({ ...formData, pattern: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white font-mono focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    placeholder={formData.is_regex ? '.*urgent.*' : 'urgent'}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                      Priorité
                    </label>
                    <input
                      type="number"
                      min={-100}
                      max={100}
                      value={formData.priority}
                      onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                      className="w-full px-3 py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-sm text-zinc-700 dark:text-zinc-300 pb-2.5">
                      <input
                        type="checkbox"
                        checked={formData.is_regex}
                        onChange={(e) => setFormData({ ...formData, is_regex: e.target.checked })}
                        className="w-4 h-4 rounded border-zinc-300 dark:border-zinc-600 text-emerald-500 focus:ring-emerald-500"
                      />
                      Expression régulière
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
                    Description (optionnel)
                  </label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    placeholder="Emails urgents"
                  />
                </div>

                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded-full px-4 py-2 border border-zinc-200/70 text-zinc-600 hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800 transition"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={createPattern.isPending || updatePattern.isPending}
                    className="rounded-full px-4 py-2 bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 flex items-center gap-2 transition"
                  >
                    {(createPattern.isPending || updatePattern.isPending) && <Loader2 className="h-4 w-4 animate-spin" />}
                    {editingPattern ? 'Modifier' : 'Ajouter'}
                  </button>
                </div>
              </form>
            )}

            {/* Patterns List */}
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
              </div>
            ) : patterns && patterns.length > 0 ? (
              <div className="space-y-3">
                {patterns.map((pattern) => (
                  <PatternRow
                    key={pattern.id}
                    pattern={pattern}
                    onEdit={() => handleEdit(pattern)}
                    onDelete={() => handleDelete(pattern)}
                    onToggleActive={() => handleToggleActive(pattern)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-100 dark:bg-emerald-900/40">
                  <Filter className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
                </div>
                <p className="text-zinc-900 dark:text-white font-medium">Aucun pattern configuré</p>
                <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">Les patterns permettent de filtrer et traiter les emails automatiquement</p>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  )
}

interface PatternRowProps {
  pattern: EmailPattern
  onEdit: () => void
  onDelete: () => void
  onToggleActive: () => void
}

function PatternRow({ pattern, onEdit, onDelete, onToggleActive }: PatternRowProps) {
  const actionConfig = actions.find((a) => a.value === pattern.action)
  const fieldConfig = fieldTypes.find((f) => f.value === pattern.field_type)

  return (
    <div className={`flex items-center gap-3 p-4 rounded-2xl border transition ${pattern.is_active ? 'bg-white dark:bg-zinc-800/80 border-emerald-100/60 dark:border-emerald-900/40' : 'bg-zinc-50 dark:bg-zinc-800/40 border-zinc-100 dark:border-zinc-800 opacity-60'}`}>
      <button
        onClick={onToggleActive}
        className={`w-5 h-5 rounded-full border-2 flex-shrink-0 transition ${pattern.is_active ? 'bg-emerald-500 border-emerald-500' : 'border-zinc-300 dark:border-zinc-600 hover:border-emerald-400'}`}
      />
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400 uppercase">
            {fieldConfig?.label}
          </span>
          {pattern.is_regex && (
            <span className="text-xs px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 rounded-full">
              regex
            </span>
          )}
          <span className={`text-xs px-2 py-0.5 rounded-full ${actionConfig?.color}`}>
            {actionConfig?.label}
          </span>
          <span className="text-xs text-zinc-400 dark:text-zinc-500">P:{pattern.priority}</span>
        </div>
        <p className="font-mono text-sm text-zinc-900 dark:text-white truncate">
          {pattern.pattern}
        </p>
        {pattern.description && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 truncate mt-0.5">
            {pattern.description}
          </p>
        )}
      </div>

      <div className="flex gap-1">
        <button
          onClick={onEdit}
          className="p-2 text-zinc-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition"
        >
          <Edit2 className="h-4 w-4" />
        </button>
        <button
          onClick={onDelete}
          className="p-2 text-zinc-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
